import React, { useState, useEffect } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { TrendingUp, TrendingDown, Target, Clock } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', purple: '#7C5CBF' };

const money = v => (v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `$${(v / 1000).toFixed(0)}K` : `$${Math.round(v || 0)}`);

const Kpi = ({ icon: Icon, label, value, sub, color, testid }) => (
  <Card className="p-4 rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid={testid}>
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-4 h-4" style={{ color }} />
      <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</span>
    </div>
    <div className="text-2xl font-bold font-mono" style={{ color }}>{value}</div>
    {sub && <div className="text-[11px] mt-0.5" style={{ color: C.slate }}>{sub}</div>}
  </Card>
);

const Panel = ({ title, subtitle, children, testid }) => (
  <Card className="p-5 rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid={testid}>
    <h3 className="text-sm font-semibold" style={{ color: C.navy }}>{title}</h3>
    {subtitle && <p className="text-[11px] mb-3" style={{ color: C.slate }}>{subtitle}</p>}
    {children}
  </Card>
);

export default function QuotePipelineAnalyticsPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/pipeline')
      .then(r => setData(r.data))
      .catch(e => console.error('pipeline analytics:', e?.message))
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-md" />)}</div>;
  if (!data) return <p className="text-sm p-6" style={{ color: C.slate }}>Analytics unavailable.</p>;

  const k = data.kpis;
  const funnel = data.funnel || [];
  const maxFunnel = funnel[0]?.count || 1;
  const reasons = data.lost_reasons || [];
  const maxReasonPremium = Math.max(...reasons.map(r => r.premium), 1);

  return (
    <div className="space-y-5" data-testid="pipeline-analytics-page">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Target} label="Win rate" value={`${k.win_rate_pct}%`} color={k.win_rate_pct >= 50 ? C.green : C.amber}
          sub={`${k.won} won of ${k.won + k.lost + k.declined} decided`} testid="kpi-win-rate" />
        <Kpi icon={TrendingUp} label="Premium won" value={money(k.won_premium)} color={C.green}
          sub={`${k.won} policies bound`} testid="kpi-premium-won" />
        <Kpi icon={TrendingDown} label="Premium leaked" value={money(k.lost_premium)} color={C.red}
          sub={`${k.lost + k.declined} quotes lost or declined`} testid="kpi-premium-lost" />
        <Kpi icon={Clock} label="Open pipeline" value={`${k.open}`} color={C.teal}
          sub={`${money(k.open_premium)} quoted${k.avg_days_to_bind ? ` · avg ${k.avg_days_to_bind}d to bind` : ''}`} testid="kpi-open-pipeline" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Conversion Funnel" subtitle="Where quotes drop out of the pipeline" testid="funnel-panel">
          <div className="space-y-3">
            {funnel.map((f, i) => {
              const pct = Math.round((f.count / maxFunnel) * 100);
              const conv = i > 0 && funnel[i - 1].count ? Math.round((f.count / funnel[i - 1].count) * 100) : null;
              const colors = [C.blue, C.teal, C.green];
              return (
                <div key={f.stage} data-testid={`funnel-stage-${i}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="font-medium" style={{ color: C.navy }}>{f.stage}</span>
                    <span style={{ color: C.slate }}>{f.count.toLocaleString()}{conv !== null && <span className="ml-2" style={{ color: colors[i] }}>{conv}%</span>}</span>
                  </div>
                  <div className="h-6 rounded-sm" style={{ background: '#EEF1F5' }}>
                    <div className="h-6 rounded-sm transition-all" style={{ width: `${Math.max(pct, 3)}%`, background: colors[i] }} />
                  </div>
                </div>
              );
            })}
            <div className="flex gap-4 text-[11px] pt-1" style={{ color: C.slate }}>
              <span><b style={{ color: C.red }}>{k.declined}</b> UW declined</span>
              <span><b style={{ color: C.red }}>{k.lost}</b> lost / expired</span>
              <span><b style={{ color: C.teal }}>{k.open}</b> still open</span>
            </div>
          </div>
        </Panel>

        <Panel title="Lost-Business Reasons" subtitle="Why premium is leaking, ranked by $ at stake" testid="lost-reasons-panel">
          {reasons.length === 0 ? (
            <p className="text-xs" style={{ color: C.slate }} data-testid="no-lost-reasons">No lost business recorded yet — mark lost quotes with a reason from the quote screen.</p>
          ) : (
            <div className="space-y-2.5">
              {reasons.map(r => (
                <div key={r.code} data-testid={`lost-reason-${r.code}`}>
                  <div className="flex justify-between text-xs mb-1">
                    <span style={{ color: C.navy }}>{r.label} <span style={{ color: C.slate }}>({r.count})</span></span>
                    <span className="font-mono font-semibold" style={{ color: C.red }}>{money(r.premium)}</span>
                  </div>
                  <div className="h-2 rounded-full" style={{ background: '#EEF1F5' }}>
                    <div className="h-2 rounded-full" style={{ width: `${Math.max((r.premium / maxReasonPremium) * 100, 2)}%`, background: r.code === 'uw_declined' ? C.slate : C.red }} />
                  </div>
                </div>
              ))}
            </div>
          )}
          {(data.competitors || []).length > 0 && (
            <div className="mt-4 pt-3" style={{ borderTop: '1px solid #EEF1F5' }} data-testid="competitors-panel">
              <p className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: C.slate }}>Lost to competitors</p>
              <div className="space-y-1.5">
                {data.competitors.map(c => (
                  <div key={c.name} className="flex justify-between text-xs" data-testid={`competitor-${c.name}`}>
                    <span style={{ color: C.navy }}>{c.name} <span style={{ color: C.slate }}>({c.count})</span>{c.avg_winning_price ? <span style={{ color: C.slate }}> · won at ~{money(c.avg_winning_price)}</span> : null}</span>
                    <span className="font-mono font-semibold" style={{ color: C.red }}>{money(c.premium)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Panel>
      </div>

      <Panel title="Monthly Trend" subtitle="Submitted vs bound vs lost — last 6 months" testid="monthly-trend-panel">
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.monthly} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: C.slate }} />
              <YAxis tick={{ fontSize: 11, fill: C.slate }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="submitted" name="Submitted" fill={C.blue} radius={[3, 3, 0, 0]} />
              <Bar dataKey="bound" name="Bound" fill={C.green} radius={[3, 3, 0, 0]} />
              <Bar dataKey="lost" name="Lost/Declined" fill={C.red} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Panel>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Win Rate by Class" subtitle="Sorted by premium leakage" testid="by-class-panel">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left" style={{ color: C.slate }}>
                <th className="pb-2 font-medium">Class</th><th className="pb-2 font-medium text-right">Quotes</th>
                <th className="pb-2 font-medium text-right">Won</th><th className="pb-2 font-medium text-right">Win %</th>
                <th className="pb-2 font-medium text-right">Leaked</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_class || []).map(r => (
                <tr key={r.class_id} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)', color: C.navy }} data-testid={`class-row-${r.class_id}`}>
                  <td className="py-1.5">{r.name}</td>
                  <td className="py-1.5 text-right font-mono">{r.submitted}</td>
                  <td className="py-1.5 text-right font-mono" style={{ color: C.green }}>{r.won}</td>
                  <td className="py-1.5 text-right font-mono" style={{ color: r.win_rate_pct == null ? C.slate : r.win_rate_pct >= 50 ? C.green : C.amber }}>{r.win_rate_pct == null ? '—' : `${r.win_rate_pct}%`}</td>
                  <td className="py-1.5 text-right font-mono" style={{ color: r.lost_premium > 0 ? C.red : C.slate }}>{money(r.lost_premium)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>

        <Panel title="Win Rate by Broker" subtitle="Top 10 by submission volume" testid="by-broker-panel">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left" style={{ color: C.slate }}>
                <th className="pb-2 font-medium">Broker</th><th className="pb-2 font-medium text-right">Quotes</th>
                <th className="pb-2 font-medium text-right">Won</th><th className="pb-2 font-medium text-right">Win %</th>
                <th className="pb-2 font-medium text-right">Leaked</th>
              </tr>
            </thead>
            <tbody>
              {(data.by_broker || []).map(r => (
                <tr key={r.broker} className="border-t" style={{ borderColor: 'rgba(136,150,168,0.1)', color: C.navy }} data-testid={`broker-row-${r.broker.replace(/\s+/g, '-').toLowerCase()}`}>
                  <td className="py-1.5">{r.broker}</td>
                  <td className="py-1.5 text-right font-mono">{r.submitted}</td>
                  <td className="py-1.5 text-right font-mono" style={{ color: C.green }}>{r.won}</td>
                  <td className="py-1.5 text-right font-mono" style={{ color: r.win_rate_pct == null ? C.slate : r.win_rate_pct >= 50 ? C.green : C.amber }}>{r.win_rate_pct == null ? '—' : `${r.win_rate_pct}%`}</td>
                  <td className="py-1.5 text-right font-mono" style={{ color: r.lost_premium > 0 ? C.red : C.slate }}>{money(r.lost_premium)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}
